import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

const TEXTURAS = [
  { value: 'none',      label: 'Sin textura',  emoji: '⬜' },
  { value: 'particles', label: 'Partículas',   emoji: '✨' },
  { value: 'waves',     label: 'Ondas',        emoji: '🌊' },
  { value: 'neural',    label: 'Red neuronal', emoji: '🕸️' },
  { value: 'aurora',    label: 'Aurora',       emoji: '🌌' },
  { value: 'matrix',    label: 'Matrix',       emoji: '💻' },
  { value: 'nebula',    label: 'Nebulosa',     emoji: '🔮' },
  { value: 'hexagons',  label: 'Hexágonos',    emoji: '⬡' },
  { value: 'circuit',   label: 'Circuito',     emoji: '⚡' },
  { value: 'rain',      label: 'Lluvia',       emoji: '🌧️' },
];

const FUENTES = [
  { value: 'modern',      label: 'Moderna',      sub: 'Profesional tech',    emoji: '💼', preview: "'Sora', sans-serif" },
  { value: 'elegant',     label: 'Elegante',     sub: 'Clasica refinada',    emoji: '👑', preview: "'Playfair Display', serif" },
  { value: 'tech',        label: 'Tecnica',      sub: 'Ingenieria / campo',  emoji: '🔧', preview: "'Rajdhani', sans-serif" },
  { value: 'rounded',     label: 'Redondeada',   sub: 'Amigable / casual',   emoji: '😊', preview: "'Nunito', sans-serif" },
  { value: 'jewelry',     label: 'Joyeria',      sub: 'Lujo / alta moda',    emoji: '💎', preview: "'Playfair Display SC', serif" },
  { value: 'sport',       label: 'Deportiva',    sub: 'Atletica / fuerte',   emoji: '🏃', preview: "'Bebas Neue', cursive" },
  { value: 'artistic',    label: 'Artistica',    sub: 'Creativa / pintura',  emoji: '🎨', preview: "'Caveat', cursive" },
  { value: 'extravagant', label: 'Extravagante', sub: 'Fluida / divertida',  emoji: '🎭', preview: "'Pacifico', cursive" },
  { value: 'executive',   label: 'Ejecutiva',    sub: 'Autoridad / abogados',emoji: '⚖️', preview: "'Libre Baskerville', serif" },
  { value: 'minimal',     label: 'Electricista', sub: 'Electrica / futurista',emoji: '⚡', preview: "'Orbitron', sans-serif" },
];

const fontLinks = {
  modern:      "https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&display=swap",
  elegant:     "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&display=swap",
  tech:        "https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&display=swap",
  rounded:     "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&display=swap",
  jewelry:     "https://fonts.googleapis.com/css2?family=Playfair+Display+SC:wght@400;700&display=swap",
  sport:       "https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap",
  artistic:    "https://fonts.googleapis.com/css2?family=Caveat:wght@400;600;700&display=swap",
  extravagant: "https://fonts.googleapis.com/css2?family=Pacifico&display=swap",
  executive:   "https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&display=swap",
  minimal:     "https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700&display=swap",
};

const EditarPerfil = ({ adminMode = false }) => {
    const { uuid }   = useParams();
    const navigate   = useNavigate();
    const fotoRef    = useRef();

    const [perfil,        setPerfil]       = useState(null);
    const [loading,       setLoading]      = useState(true);
    const [saving,        setSaving]       = useState(false);
    const [uploadingFoto, setUploadingFoto]= useState(false);
    const [mensaje,       setMensaje]      = useState(null);
    const [error,         setError]        = useState(null);
    const [tab,           setTab]          = useState('datos');

    const apiSave = adminMode ? `/tarjetas/${uuid}/perfil` : `/perfil/${uuid}`;
    const backUrl  = adminMode ? '/tarjetas' : '/mis-tarjetas';

    useEffect(() => {
        api.get(`/perfil/${uuid}`)
            .then(({ data }) => setPerfil(data))
            .catch(() => navigate(backUrl))
            .finally(() => setLoading(false));
    }, [uuid, navigate, backUrl]);

    useEffect(() => {
        if (!perfil) return;
        const fuente = perfil.fuente || 'modern';
        if (!fontLinks[fuente]) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet'; link.href = fontLinks[fuente];
        document.head.appendChild(link);
        return () => { try { document.head.removeChild(link); } catch(e){} };
    }, [perfil]);

    const handleChange = (e) => setPerfil(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSave = async () => {
        setSaving(true); setMensaje(null); setError(null);
        try {
            await api.put(apiSave, perfil);
            setMensaje('Perfil guardado correctamente');
            setTimeout(() => setMensaje(null), 3000);
        } catch (e) {
            setError(e.response?.data?.error || 'Error guardando el perfil');
        } finally { setSaving(false); }
    };

    const handleFoto = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingFoto(true);
        const form = new FormData();
        form.append('foto', file);
        try {
            const { data } = await api.post(`/perfil/${uuid}/foto`, form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setPerfil(prev => ({ ...prev, foto_url: data.foto_url }));
        } catch (e) {
            setError(e.response?.data?.error || 'Error subiendo foto');
        } finally { setUploadingFoto(false); }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="text-gray-400 text-sm">Cargando perfil...</div>
        </div>
    );

    const previewUrl   = `${window.location.origin}/c/${perfil?.codigo}`;
    const acento       = perfil?.color_acento || '#00d4ff';
    const fondoColor   = perfil?.color_fondo  || '#0a0f1e';
    const fuenteActual = FUENTES.find(f => f.value === perfil?.fuente) || FUENTES[0];

    const inputCls = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white transition";
    const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5";

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-5 flex items-center gap-3">
                <button onClick={() => navigate(backUrl)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600">←</button>
                <div>
                    <h1 className="text-xl font-bold text-gray-800">{adminMode ? 'Editar Perfil (Admin)' : 'Mi Tarjeta Digital'}</h1>
                    <p className="text-xs text-gray-400 font-mono">{perfil?.codigo}</p>
                </div>
                <a href={previewUrl} target="_blank" rel="noreferrer"
                   className="ml-auto flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 font-medium">
                    Ver público →
                </a>
            </div>

            {mensaje && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-xl text-sm border border-green-200 font-medium">{mensaje}</div>}
            {error   && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-xl text-sm border border-red-200">{error}</div>}

            <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1">
                {[
                    { id: 'datos',  label: '👤 Datos' },
                    { id: 'redes',  label: '🔗 Redes' },
                    { id: 'diseno', label: '🎨 Diseño' },
                ].map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                            tab === t.id ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'
                        }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">

                {tab === 'datos' && (
                    <div className="space-y-5">
                        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                            <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0"
                                 style={{ border:`3px solid ${acento}` }}>
                                {perfil?.foto_url ? (
                                    <img src={perfil.foto_url} alt="Foto" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-2xl bg-gray-200 text-gray-400">👤</div>
                                )}
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-700 mb-1">Foto de perfil</p>
                                <p className="text-xs text-gray-400 mb-2">JPG, PNG o WEBP — max 3MB</p>
                                <button onClick={() => fotoRef.current.click()} disabled={uploadingFoto}
                                    className="px-3 py-1.5 text-xs bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-gray-700 disabled:opacity-50 font-medium">
                                    {uploadingFoto ? 'Subiendo...' : 'Cambiar foto'}
                                </button>
                                <input ref={fotoRef} type="file" accept="image/*" className="hidden" onChange={handleFoto} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className={labelCls}>Nombre</label><input name="nombre" value={perfil?.nombre||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>Apellido</label><input name="apellido" value={perfil?.apellido||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>Cargo</label><input name="cargo" value={perfil?.cargo||''} onChange={handleChange} className={inputCls} placeholder="Ej: Ingeniero, CEO, Artista"/></div>
                            <div><label className={labelCls}>Empresa</label><input name="empresa" value={perfil?.empresa||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>Telefono</label><input name="telefono" value={perfil?.telefono||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>WhatsApp</label><input name="whatsapp" value={perfil?.whatsapp||''} onChange={handleChange} placeholder="573001234567" className={inputCls}/></div>
                            <div><label className={labelCls}>Email</label><input name="email" type="email" value={perfil?.email||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>Sitio web</label><input name="website" value={perfil?.website||''} onChange={handleChange} placeholder="https://miweb.com" className={inputCls}/></div>
                        </div>
                        <div>
                            <label className={labelCls}>Biografia profesional</label>
                            <textarea name="bio" value={perfil?.bio||''} onChange={handleChange} rows={3}
                                placeholder="Cuentale al mundo quien eres..."
                                className={`${inputCls} resize-none`}/>
                        </div>
                    </div>
                )}

                {tab === 'redes' && (
                    <div className="space-y-4">
                        <p className="text-xs text-gray-400 bg-gray-50 rounded-xl p-3">
                            Pega la URL completa. Las que esten vacias no apareceran en tu perfil publico.
                        </p>
                        {[
                            { name: 'linkedin',  label: 'LinkedIn',  placeholder: 'https://linkedin.com/in/usuario', color: '#0A66C2' },
                            { name: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/usuario',   color: '#E1306C' },
                            { name: 'tiktok',    label: 'TikTok',    placeholder: 'https://tiktok.com/@usuario',     color: '#010101' },
                            { name: 'youtube',   label: 'YouTube',   placeholder: 'https://youtube.com/@canal',      color: '#FF0000' },
                        ].map(r => (
                            <div key={r.name}>
                                <label className="block text-sm font-semibold mb-1.5" style={{color: r.color}}>
                                    {r.label} {perfil?.[r.name] && <span className="text-xs text-green-500 font-normal">Activo</span>}
                                </label>
                                <input name={r.name} value={perfil?.[r.name]||''} onChange={handleChange}
                                    placeholder={r.placeholder} className={inputCls}/>
                            </div>
                        ))}
                    </div>
                )}

                {tab === 'diseno' && (
                    <div className="space-y-6">
                        <div className="rounded-2xl p-5 flex items-center gap-4"
                             style={{ background: fondoColor, minHeight: 80 }}>
                            <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-white text-lg"
                                 style={{ border:`2px solid ${acento}`, background:`${acento}33` }}>
                                {perfil?.nombre?.charAt(0)||'?'}
                            </div>
                            <div className="flex-1">
                                <div className="text-base font-bold text-white" style={{fontFamily: fuenteActual.preview}}>
                                    {perfil?.nombre||'Nombre'} {perfil?.apellido||'Apellido'}
                                </div>
                                <div className="text-xs" style={{color: acento}}>{perfil?.cargo||'Tu cargo aqui'}</div>
                            </div>
                            <div className="px-3 py-1.5 rounded-lg text-xs font-bold text-black" style={{background: acento}}>Boton</div>
                        </div>

                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-3">Colores</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>Fondo</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" name="color_fondo" value={perfil?.color_fondo||'#0a0f1e'} onChange={handleChange}
                                            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 flex-shrink-0"/>
                                        <input name="color_fondo" value={perfil?.color_fondo||'#0a0f1e'} onChange={handleChange}
                                            className={`${inputCls} flex-1`} placeholder="#0a0f1e"/>
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls}>Acento</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" name="color_acento" value={perfil?.color_acento||'#00d4ff'} onChange={handleChange}
                                            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 flex-shrink-0"/>
                                        <input name="color_acento" value={perfil?.color_acento||'#00d4ff'} onChange={handleChange}
                                            className={`${inputCls} flex-1`} placeholder="#00d4ff"/>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-3">Textura de fondo</p>
                            <div className="grid grid-cols-2 gap-2">
                                {TEXTURAS.map(t => (
                                    <button key={t.value} onClick={() => setPerfil(p => ({...p, textura: t.value}))}
                                        className={`flex items-center gap-2 p-3 rounded-xl border text-sm transition-all text-left ${
                                            perfil?.textura === t.value
                                                ? 'border-blue-400 bg-blue-50 text-blue-700 font-semibold'
                                                : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                        }`}>
                                        <span className="text-lg">{t.emoji}</span>
                                        <span>{t.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-3">Fuente tipografica</p>
                            <div className="space-y-2">
                                {FUENTES.map(f => (
                                    <button key={f.value} onClick={() => setPerfil(p => ({...p, fuente: f.value}))}
                                        className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                                            perfil?.fuente === f.value
                                                ? 'border-blue-400 bg-blue-50'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">{f.emoji}</span>
                                            <div>
                                                <span className="text-xs text-gray-500 block">{f.label}</span>
                                                <span className="text-xs text-gray-400">{f.sub}</span>
                                            </div>
                                        </div>
                                        <span className="text-base text-gray-800 ml-4 flex-shrink-0" style={{fontFamily: f.preview}}>
                                            Hola Mundo
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-between items-center pt-5 mt-5 border-t border-gray-100">
                    <a href={previewUrl} target="_blank" rel="noreferrer"
                       className="text-sm text-blue-500 hover:text-blue-600 font-medium">
                        Ver perfil publico
                    </a>
                    <button onClick={handleSave} disabled={saving}
                        className="px-6 py-2.5 rounded-xl text-white font-semibold text-sm disabled:opacity-50 transition-all hover:opacity-90"
                        style={{ backgroundColor: 'var(--color-primary)' }}>
                        {saving ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditarPerfil;
